import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HOME_BY_ROLE = { admin: "/admin", teacher: "/teacher", student: "/student", parent: "/parent" };

export const AuthShell = ({ children, heading, sub }) => (
  <div className="min-h-screen flex bg-white">
    <div className="hidden lg:block lg:w-1/2 relative">
      <img
        src="https://images.pexels.com/photos/31656148/pexels-photo-31656148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt="University architecture" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-slate-950/60" />
      <div className="absolute bottom-10 left-10 right-10 text-white">
        <p className="font-heading text-3xl font-semibold tracking-tight">Attendance, marked with certainty.</p>
        <p className="mt-2 text-slate-300 text-sm">Daily period-wise attendance for university departments.</p>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="h-9 w-9 bg-slate-950 text-white flex items-center justify-center rounded-sm">
            <GraduationCap className="h-5 w-5" strokeWidth={2} />
          </div>
          <span className="font-heading font-bold text-xl tracking-tight text-slate-950">Law-Edu-Management</span>
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-950" data-testid="auth-heading">{heading}</h1>
        <p className="text-sm text-slate-500 mt-1 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      const { data } = await import("@/lib/api").then((m) => m.api.get("/auth/me"));
      navigate(HOME_BY_ROLE[data.role] || "/login");
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell heading="Sign in" sub="Use your university account credentials. Parents can also sign in with their registered mobile number.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && (
          <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email or mobile number</Label>
          <Input id="email" data-testid="login-email-input" type="text" required autoComplete="username"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <Button type="submit" data-testid="login-submit-btn" disabled={loading}
          className="w-full bg-slate-950 hover:bg-slate-800 text-white transition-colors duration-200">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
        <div className="text-right">
          <Link to="/forgot-password" data-testid="forgot-password-link"
            className="text-sm text-[#002FA7] hover:underline">
            Forgot password?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
